import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { getCurrentUser } from '../../../redux/slices/authSlice';
import { changePassword } from '../../../redux/slices/userSlice';

const SecurityArea = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.user);

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);

    useEffect(() => {
        if (!currentUser) {
            dispatch(getCurrentUser());
        }
    }, [dispatch, currentUser]);

    const togglePasswordVisibility = () => setShowPasswords(!showPasswords);

    const validations = {
        length: newPassword.length >= 8,
        uppercase: /[A-Z]/.test(newPassword),
        lowercase: /[a-z]/.test(newPassword),
        number: /\d/.test(newPassword),
        match: newPassword === confirmPassword && newPassword.length > 0,
        differentFromOld: newPassword !== oldPassword || newPassword.length === 0
    };

    const isFormValid = 
        oldPassword.length > 0 &&
        validations.length &&
        validations.uppercase &&
        validations.lowercase &&
        validations.number &&
        validations.match &&
        validations.differentFromOld;

    const handleConfirm = async () => {
        if (!isFormValid) return;
        
        try {
            await dispatch(changePassword({ current_password: oldPassword, new_password: newPassword })).unwrap();
            toast.success(t('components.SecurityArea.password_updated', 'Password atualizada com sucesso!'));
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error("Password change error:", error);
            toast.error(
                error?.response?.data?.detail ||
                error?.message ||
                t('components.SecurityArea.password_error', 'Erro ao atualizar password.')
            );
        }
    };

    const ValidationItem = ({ isValid, text }) => (
        <div className={`d-flex align-items-center gap-2 mb-1 ${isValid ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.85rem', transition: 'color 0.3s' }}>
            {isValid ? <FaCheckCircle /> : <FaTimesCircle />}
            <span>{text}</span>
        </div>
    );

    return (
        <div className="pt-4 pb-5">
            <Row className="gx-5">
                <Col md={6} className="mb-5 mb-md-0">
                    <h4 className="txt-main fw-bold mb-4">{t('components.SecurityArea.change_password')}</h4>
                    <Form>
                        <Form.Group className="mb-4">
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>
                                {t('components.SecurityArea.current_password')}
                            </Form.Label>
                            <Form.Control 
                                type={showPasswords ? "text" : "password"}
                                placeholder={t('components.SecurityArea.current_pass_placeholder')}
                                value={oldPassword} 
                                onChange={(e) => setOldPassword(e.target.value)}
                                className="bg-surface txt-main py-2"
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-2">
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>
                                {t('components.SecurityArea.new_password')}
                            </Form.Label>
                            <Form.Control 
                                type={showPasswords ? "text" : "password"}
                                placeholder={t('components.SecurityArea.new_pass_placeholder')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="bg-surface txt-main py-2"
                            />
                        </Form.Group>

                        <div className="mb-4 px-2 py-3 rounded" style={{ backgroundColor: 'var(--gray-50)', border: '1px solid var(--gray-200)' }}>
                            <p className="txt-main fw-bold mb-2" style={{ fontSize: '0.85rem' }}>
                                {t('components.SecurityArea.password_requirements', 'A tua password deve conter:')}
                            </p>
                            <ValidationItem 
                                isValid={validations.length} 
                                text={t('components.SecurityArea.req_length', 'Pelo menos 8 caracteres')} 
                            />
                            <ValidationItem 
                                isValid={validations.uppercase} 
                                text={t('components.SecurityArea.req_uppercase', 'Pelo menos uma letra maiúscula')} 
                            />
                            <ValidationItem 
                                isValid={validations.lowercase} 
                                text={t('components.SecurityArea.req_lowercase', 'Pelo menos uma letra minúscula')} 
                            />
                            <ValidationItem 
                                isValid={validations.number} 
                                text={t('components.SecurityArea.req_number', 'Pelo menos um número')} 
                            />
                            {newPassword.length > 0 && !validations.differentFromOld && (
                                <div className="text-danger mt-2" style={{ fontSize: '0.85rem' }}>
                                    <FaTimesCircle className="me-2" />
                                    {t('components.SecurityArea.req_different', 'A nova password deve ser diferente da atual.')}
                                </div>
                            )}
                        </div>

                        <Form.Group className="mb-4">
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>
                                {t('components.SecurityArea.confirm_password')}
                            </Form.Label>
                            <Form.Control 
                                type={showPasswords ? "text" : "password"}
                                placeholder={t('components.SecurityArea.confirm_pass_placeholder')}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="bg-surface txt-main py-2"
                            />
                            {confirmPassword.length > 0 && (
                                <div className={`mt-2 ${validations.match ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.85rem' }}>
                                    {validations.match 
                                        ? <><FaCheckCircle className="me-1"/> {t('components.SecurityArea.passwords_match', 'As passwords coincidem')}</>
                                        : <><FaTimesCircle className="me-1"/> {t('components.SecurityArea.passwords_not_match', 'As passwords não coincidem')}</>
                                    }
                                </div>
                            )}
                        </Form.Group>

                        <Form.Check
                            type="checkbox"
                            label={t('components.SecurityArea.show_passwords')}
                            checked={showPasswords}
                            onChange={togglePasswordVisibility}
                            className="txt-subtle mb-4"
                        />
                        
                        <Button 
                            className="btn btn-primary px-5 py-2 fw-bold w-100" 
                            onClick={handleConfirm}
                            disabled={!isFormValid}
                        >
                            {t('components.SecurityArea.confirm_btn')}
                        </Button>
                    </Form>
                </Col>
                <Col md={6}>
                    <h4 className="txt-main fw-bold mb-4">{t('components.SecurityArea.two_factor')}</h4>
                    <Form>
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="txt-subtle fw-semibold mb-0" style={{ fontSize: '0.85rem' }}>
                                    {t('components.SecurityArea.via_sms')}
                                </Form.Label>
                                <Form.Check type="switch" id="sms-switch" />
                            </div>
                            <Form.Control 
                                type="text" 
                                placeholder={t('components.SecurityArea.phone_placeholder')}
                                className="bg-surface txt-main py-2" 
                            />
                        </div>

                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="txt-subtle fw-semibold mb-0" style={{ fontSize: '0.85rem' }}>
                                    {t('components.SecurityArea.via_email')}
                                </Form.Label>
                                <Form.Check type="switch" id="email-switch" />
                            </div>
                            <Form.Control 
                                type="email" 
                                placeholder={t('components.SecurityArea.email_placeholder')}
                                className="bg-surface txt-main py-2" 
                            />
                        </div>
                    </Form>
                </Col>
            </Row>
        </div>
    );
};

export default SecurityArea;